import {DestroyRef, inject, Injectable, OnDestroy} from '@angular/core';
import {firstValueFrom, Observable, Subject} from 'rxjs';

import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {IResponseApi} from '@core/models';
import {MUTE_TYPE, MuteTypeValue} from '@features/conversation/models/conversation-constants.model';

/**
 * Sự kiện mute auto-hết hạn (timer client-side reset).
 * Component conversation-list/right-panel subscribe để update UI khi mute tự hết.
 */
export interface IMuteExpiredEvent {
  conversationId: string;
  pageId: string;
  at: number; // epoch ms khi event phát ra
}

interface IMuteResult {
  /** Thời điểm mute hết hạn (epoch ms). `null` = mute vĩnh viễn. */
  muteUntil: number | null;
  raw: IResponseApi;
}

/**
 * Quản lý mute/unmute hội thoại + lịch timer auto-reset UI.
 *
 * Logic port từ `muteTimers` trong `vpage-fe1` (`conversation.component.ts`):
 * - Khi mute với khoảng thời gian hữu hạn (1h, 4h, until8AM): schedule
 * `setTimeout` tới mốc hết hạn, phát `expired$` để UI tự bỏ icon mute.
 * - Khi unmute / mute forever: clear timer hiện có cho conversation đó.
 * - Khi service destroy: clear toàn bộ timer (tránh leak).
 *
 * Service NÀY là **stateless theo conversation** — không lưu trạng thái mute,
 * chỉ lưu handle timer. Trạng thái muteType/muteUntil được store ở
 * `IConversation` / state component như cũ.
 */
@Injectable({providedIn: 'any'})
export class MuteConversationService implements OnDestroy {
  private readonly api = inject(ConversationApiService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly expiredSubject = new Subject<IMuteExpiredEvent>();

  /** Stream phát khi timer mute hết hạn — UI bind để update icon. */
  readonly expired$: Observable<IMuteExpiredEvent> = this.expiredSubject.asObservable();

  constructor() {
    this.destroyRef.onDestroy(() => this.clearAllTimers());
  }

  ngOnDestroy(): void {
    this.clearAllTimers();
  }

  // ─── API ─────────────────────────────────────────────────────────────────

  /**
   * Bật mute cho hội thoại.
   *
   * @param pageId ID page
   * @param conversationId ID hội thoại
   * @param muteType 1 trong 4 giá trị `MUTE_TYPE`
   * @returns muteUntil (epoch ms) — `null` nếu mute forever.
   */
  async mute(
    pageId: string,
    conversationId: string,
    muteType: MuteTypeValue,
  ): Promise<IMuteResult> {
    const res = await firstValueFrom(
      this.api.handleMutedConversation('mute', {
        pageId,
        id: conversationId,
        muteType,
      }),
    );

    const muteUntil = this.computeMuteUntil(muteType);
    this.scheduleExpiry(pageId, conversationId, muteUntil);

    return {muteUntil, raw: res};
  }

  /** Tắt mute cho hội thoại. Đồng thời xoá timer auto-reset (nếu có). */
  async unmute(pageId: string, conversationId: string): Promise<IResponseApi> {
    this.clearTimer(conversationId);
    return firstValueFrom(
      this.api.handleMutedConversation('unmute', {
        pageId,
        id: conversationId,
      }),
    );
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Khôi phục timer khi load lại danh sách hội thoại (BE trả `muteUntilTimestamp`).
   * Component gọi method này cho mỗi conv đang còn mute hữu hạn.
   */
  restoreTimer(pageId: string, conversationId: string, muteUntilMs: number): void {
    this.scheduleExpiry(pageId, conversationId, muteUntilMs);
  }

  /** Tính mốc kết thúc mute (epoch ms) từ `muteType` của BE. */
  computeMuteUntil(muteType: MuteTypeValue): number | null {
    const now = Date.now();
    switch (muteType) {
      case MUTE_TYPE.MUTE_ONE_HOUR:
      case MUTE_TYPE.MUTE_FOUR_HOURS:
        // BE trả số giây → cộng vào now để ra epoch ms.
        return now + (muteType as number) * 1000;

      case MUTE_TYPE.MUTE_UNTIL_8AM: {
        // 8h sáng hôm sau theo timezone client.
        const next = new Date();
        next.setHours(8, 0, 0, 0);
        if (next.getTime() <= now) {
          next.setDate(next.getDate() + 1);
        }
        return next.getTime();
      }

      case MUTE_TYPE.MUTE_FOREVER:
      default:
        return null;
    }
  }

  private scheduleExpiry(
    pageId: string,
    conversationId: string,
    muteUntilMs: number | null,
  ): void {
    this.clearTimer(conversationId);
    if (muteUntilMs === null) {
      return; // mute forever — không timer
    }

    const delay = muteUntilMs - Date.now();
    if (delay <= 0) {
      // Đã quá hạn → phát ngay
      this.expiredSubject.next({conversationId, pageId, at: Date.now()});
      return;
    }

    const handle = setTimeout(() => {
      this.timers.delete(conversationId);
      this.expiredSubject.next({conversationId, pageId, at: Date.now()});
    }, delay);

    this.timers.set(conversationId, handle);
  }

  private clearTimer(conversationId: string): void {
    const handle = this.timers.get(conversationId);
    if (handle !== undefined) {
      clearTimeout(handle);
      this.timers.delete(conversationId);
    }
  }

  private clearAllTimers(): void {
    for (const handle of this.timers.values()) {
      clearTimeout(handle);
    }
    this.timers.clear();
  }
}
